import { Link } from "react-router";
import {
	Sheet,
	SheetContent,
	SheetDescription,
	SheetHeader,
	SheetTitle,
} from "@/components/ui/sheet";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import ThemeSelector from "./theme-selector";
import { Button } from "@/components/ui/button";
import { ChevronDown, LogIn, LogOut, UserPlus } from "lucide-react";
import { useApp } from "@/app/providers/app-provider";
import { buzzHomeUrl } from "@/features/the-buzz/the-buzz-api";
import { useHasUnseenRewards } from "@/hooks/rewards-hooks";
import { useState } from "react";

interface NavDrawerProps {
	isOpen: boolean;
	setIsOpen: (isOpen: boolean) => void;
}

export function MobileNavDrawer({ isOpen, setIsOpen }: NavDrawerProps) {
	const { authManager, appStore, isSubscribed } = useApp();
	const isLoggedIn = !!appStore.session?.user?.id;
	const { data: unseenData } = useHasUnseenRewards();
	const [isMadeForOpen, setIsMadeForOpen] = useState(false);

	const navItems = [
		{ href: "/about", label: "About" },
		{ href: "/pricing", label: "Pricing" },
		{ href: `${buzzHomeUrl}/help/`, label: "Support", external: true },
		{ href: `${buzzHomeUrl}`, label: "The Buzz", external: true },
	];

	const madeForPersonas = [
		{ href: "/made-for/writers", label: "Writers" },
		{ href: "/made-for/students", label: "Students" },
		{ href: "/made-for/remote-workers", label: "Remote Workers" },
		{ href: "/made-for/developers", label: "Developers" },
		{ href: "/made-for/teachers", label: "Teachers" },
	];

	return (
		<Sheet open={isOpen} onOpenChange={setIsOpen}>
			<SheetContent
				side="right"
				className="w-[300px] sm:w-[400px] bg-background border-none"
			>
				<div className="h-full flex flex-col justify-between overflow-y-scroll">
					<div>
						<ThemeSelector />
						<SheetHeader className="hidden">
							{/* Necessary for screen readers */}
							<SheetTitle>Navigation</SheetTitle>
							<SheetDescription>
								Select your theme, logout, or navigate to a different page.
							</SheetDescription>
						</SheetHeader>
						<nav className="flex flex-col gap-4 mt-12">
							{navItems.map((item) =>
								item.external ? (
									<a
										key={item.href}
										href={item.href}
										target="_blank"
										rel="noopener noreferrer"
										className="text-3xl font-medium pb-2"
										onClick={() => setIsOpen(false)}
									>
										{item.label}
									</a>
								) : (
									<Link
										key={item.href}
										to={item.href}
										className="text-3xl font-medium pb-2"
										onClick={() => setIsOpen(false)}
									>
										{item.label}
									</Link>
								),
							)}

							{isLoggedIn && !isSubscribed && (
								<Link
									to="/rewards"
									className="text-3xl font-medium pb-2 flex items-center gap-2"
									onClick={() => setIsOpen(false)}
								>
									{unseenData?.hasUnseenRewards && (
										<span className="h-3 w-3 rounded-full bg-primary" />
									)}
									<span>Rewards</span>
								</Link>
							)}

							{/* Made For Dropdown */}
							<Collapsible open={isMadeForOpen} onOpenChange={setIsMadeForOpen}>
								<CollapsibleTrigger className="flex items-center justify-between text-3xl font-medium pb-2 w-full">
									<span>Made For</span>
									<ChevronDown
										className={`h-6 w-6 transition-transform ${isMadeForOpen ? "rotate-180" : ""}`}
									/>
								</CollapsibleTrigger>
								<CollapsibleContent className="flex flex-col gap-3 mt-3 ml-4">
									{madeForPersonas.map((persona) => (
										<Link
											key={persona.href}
											to={persona.href}
											className="text-2xl font-normal text-muted-foreground hover:text-foreground transition-colors"
											onClick={() => setIsOpen(false)}
										>
											{persona.label}
										</Link>
									))}
								</CollapsibleContent>
							</Collapsible>
						</nav>
					</div>
					{appStore.session ? (
						<Button variant="secondary" onClick={authManager.signOut}>
							<span>Logout</span>
							<LogOut className="h-6 w-6" />
						</Button>
					) : (
						<div className="flex flex-col gap-3">
							<Link to="/auth/log-in" onClick={() => setIsOpen(false)}>
								<Button variant="secondary" className="w-full" size="lg">
									<span>Log In</span>
									<LogIn className="h-6 w-6" />
								</Button>
							</Link>
							<Link to="/auth/sign-up" onClick={() => setIsOpen(false)}>
								<Button variant="primary" className="w-full" size="lg">
									<span>Sign Up</span>
									<UserPlus className="h-6 w-6" />
								</Button>
							</Link>
						</div>
					)}
				</div>
			</SheetContent>
		</Sheet>
	);
}
